/**
 * @fileoverview This file defines `OpenAiLlmApi`, an OpenAI API client for interacting with GPT models via methods like `chatCompletion`, `chatCompletionWithJsonOutput`, and `streamChatCompletions`. It also includes `promptOpenaiWithLooping` for generating long responses and tracks token usage. The file defines types like `Message`, `InputTool`, and `Usage`.
 */

import type { ApiKeys, KeyProvider } from './api_keys'
import type {
  InputTool,
  LlmApi,
  Message,
  ModelNeedingKeys,
  ResponseToolCall,
  Usage,
} from './llm_types'
import { debug_block } from '../util/logging'
import { tokenCount } from '../util/tokens'
import OpenAI from 'openai'
import type {
  ChatCompletionChunk,
  ChatCompletionTool,
  ResponseFormatJSONObject,
} from 'openai/resources'
import { Stream } from 'openai/streaming'
import { openai } from '@ai-sdk/openai'
import { generateText } from 'ai'

export const Gpt5Model: ModelNeedingKeys = {
  name: 'gpt-5-2025-08-07',
  promptTokenCost: 1.25 / 1000000,
  completionTokenCost: 10 / 1000000,
  getApi: (settings: KeyProvider) => new OpenAiLlmApi('gpt-5', settings),
}

export const Gpt41Model: ModelNeedingKeys = {
  name: 'gpt-4.1',
  promptTokenCost: 2 / 1000000,
  completionTokenCost: 8 / 1000000,
  getApi: (settings: KeyProvider) => new OpenAiLlmApi('gpt-4.1', settings),
}

export const Gpt4oModel: ModelNeedingKeys = {
  name: 'gpt-4o',
  promptTokenCost: 2.5 / 1000000,
  completionTokenCost: 10 / 1000000,
  getApi: (settings: KeyProvider) => new OpenAiLlmApi('gpt-4o', settings),
}

export const Gpt4oMiniModel: ModelNeedingKeys = {
  name: 'gpt-4o-mini',
  promptTokenCost: 0.15 / 1000000,
  completionTokenCost: 0.6 / 1000000,
  getApi: (settings: KeyProvider) => new OpenAiLlmApi('gpt-4o-mini', settings),
}

export class OpenAiLlmApi implements LlmApi {
  requiredKeys: ApiKeys[] = ['OPENAI_API_KEY', 'BRAINTRUST_API_KEY']
  private client: OpenAI

  constructor(
    private modelName: 'gpt-5' | 'gpt-4o' | 'gpt-4o-mini' | 'gpt-4.1',
    settings: KeyProvider,
  ) {
    const apiKey =
      // 10/6 Braintrust seems to be having some issues (eg braintrust.dev not found)
      settings.get('OPENAI_API_KEY')

    if (!apiKey) {
      throw new Error(
        'No API key found for OpenAI. Please set OPENAI_API_KEY or BRAINTRUST_API_KEY and configure Braintrust with your OpenAI API key',
      )
    }

    this.client = new OpenAI({
      baseURL: 'https://api.braintrust.dev/v1/proxy',
      apiKey: apiKey,
    })
  }

  async chatCompletion(
    system: string,
    texts: string[],
    tools: InputTool[],
    conversation: Message[],
  ): Promise<{
    message: Message
    toolCalls: Array<ResponseToolCall>
    usage: Usage
  }> {
    const toolsForApi = this.toolsToTools(tools)

    const payload = {
      model: this.modelName,
      messages: this.argsToMessages(system, texts, conversation),
      tools: toolsForApi.length > 0 ? toolsForApi : undefined,
      seed: 1,
      // max_tokens: 4096,
    }
    debug_block('gpt-4o request', payload)
    const response = await this.client.chat.completions.create(payload)
    debug_block('gpt-4o response', response)

    const output = response.choices[0]?.message?.content || ''
    const toolCalls = response.choices[0]?.message?.tool_calls || []

    return {
      message: { role: 'assistant', content: output },
      toolCalls: toolCalls.map((toolCall) => ({
        name: toolCall.function.name,
        args: toolCall.function.arguments,
      })),
      usage: {
        promptTokens: response.usage?.prompt_tokens || 0,
        completionTokens: response.usage?.completion_tokens || 0,
      },
    }
  }

  async chatCompletionWithJsonOutput(
    system: string,
    texts: string[],
    conversation: Message[],
  ): Promise<{ result: any; usage: Usage }> {
    const payload = {
      model: this.modelName,
      messages: this.argsToMessages(system, texts, conversation),
      seed: 1,
      // max_tokens: 4096,
      response_format: { type: 'json_object' } as ResponseFormatJSONObject,
    }
    debug_block('gpt-4o request', payload)
    const response = await this.client.chat.completions.create(payload)
    debug_block('gpt-4o response', response)

    const output: object | string = response.choices[0]?.message?.content || ''

    return {
      result: JSON.parse(output),
      usage: {
        promptTokens: response.usage?.prompt_tokens || 0,
        completionTokens: response.usage?.completion_tokens || 0,
      },
    }
  }

  async streamChatCompletions(
    system: string | undefined,
    texts: string[],
    conversation: Message[],
    handler: (message: string) => void,
    prediction?: string,
  ): Promise<{ message: Message; usage: Usage; inputMessages: Message[] }> {
    const inputMessages = this.argsToMessages(system, texts, conversation)
    const payload: OpenAI.Chat.Completions.ChatCompletionCreateParamsStreaming = {
      model: this.modelName,
      messages: inputMessages,
      stream: true,
      seed: 1,
      // max_tokens: 4096,
    }

    if (prediction) {
      payload.prediction = {
        type: 'content',
        content: prediction,
      }
    }
    const stream = (await this.client.chat.completions.create(
      payload,
    )) as Stream<ChatCompletionChunk>

    let fullOutput = ''
    for await (const chunk of stream) {
      const content = chunk.choices[0]?.delta?.content || ''
      fullOutput += content
      handler(content)
    }

    return {
      message: { role: 'assistant', content: fullOutput },
      usage: {
        promptTokens: tokenCount(inputMessages.map((m) => m.content).join('\n')),
        completionTokens: tokenCount(fullOutput),
      },
      inputMessages: inputMessages as Message[],
    }
  }

  private argsToMessages(
    system: string | undefined,
    texts: string[],
    conversation: Message[],
  ): Array<
    | OpenAI.Chat.Completions.ChatCompletionSystemMessageParam
    | OpenAI.Chat.Completions.ChatCompletionAssistantMessageParam
    | OpenAI.Chat.Completions.ChatCompletionUserMessageParam
  > {
    const textMessages: OpenAI.Chat.Completions.ChatCompletionSystemMessageParam[] = texts.map(
      (text) => ({ role: 'system', content: text }),
    )
    if (system) {
      return [{ role: 'system', content: system }, ...textMessages, ...conversation]
    } else {
      return [...textMessages, ...conversation]
    }
  }

  private toolsToTools(tools: InputTool[]): ChatCompletionTool[] {
    return tools.map((tool) => ({
      type: 'function',
      function: {
        name: tool.name,
        description: tool.description,
        parameters: tool.parameters,
      },
    }))
  }
}

// Generate a single long response for the model.
export async function promptOpenaiWithLooping(
  model: 'gpt-4o-mini' | 'gpt-4o',
  system: string,
  prompt: string,
): Promise<{ text: string; usage: Usage }> {
  const { text, usage } = await generateText({
    model: openai(model),
    system,
    prompt,
    experimental_continueSteps: true,
    maxSteps: 10,
  })
  return { text, usage }
}
